import React, { useEffect, useState } from 'react';
import { Card, Table, Spinner, Alert, Form, Pagination } from 'react-bootstrap';
import EventService from "../../services/EventService.js";

const convertUTCToIST_HHMMSS = (utcTimeString) => {
  if (!utcTimeString || typeof utcTimeString !== 'string') return '';
  const [hours, minutes, seconds = 0] = utcTimeString.split(':').map(Number);
  if ([hours, minutes, seconds].some((value) => Number.isNaN(value))) return utcTimeString;

  const utcDate = new Date(Date.UTC(1970, 0, 1, hours, minutes, seconds));
  const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);

  const istHours = String(istDate.getUTCHours()).padStart(2, '0');
  const istMinutes = String(istDate.getUTCMinutes()).padStart(2, '0');
  const istSeconds = String(istDate.getUTCSeconds()).padStart(2, '0');
  return `${istHours}:${istMinutes}:${istSeconds}`;
};

const formatTo12Hour = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return '';
  const [rawHours, rawMinutes] = timeString.split(':');
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeString;

  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  return `${String(hour12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${suffix}`;
};

const getLocalDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDatePart = (rawDate) => {
  if (!rawDate) return null;
  if (typeof rawDate === 'string') {
    const match = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      };
    }
  }

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1,
    day: parsed.getDate(),
  };
};

const parseTimeTo24Hour = (rawTime) => {
  if (!rawTime) return null;
  const normalized = rawTime.trim();

  let match = normalized.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (match) {
    return {
      hours: Number(match[1]),
      minutes: Number(match[2]),
      seconds: Number(match[3] || 0),
    };
  }

  match = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    let hours = Number(match[1]) % 12;
    if (match[3].toUpperCase() === 'PM') hours += 12;
    return {
      hours,
      minutes: Number(match[2]),
      seconds: 0,
    };
  }

  return null;
};

const getEventStartDateTime = (event) => {
  const datePart = parseDatePart(event?.startDate);
  if (!datePart) return null;

  const istTime = convertUTCToIST_HHMMSS(event?.eventTime);
  const timePart = parseTimeTo24Hour(istTime || event?.eventTime || '');
  const hours = timePart?.hours ?? 0;
  const minutes = timePart?.minutes ?? 0;
  const seconds = timePart?.seconds ?? 0;

  return new Date(
    datePart.year,
    datePart.month - 1,
    datePart.day,
    hours,
    minutes,
    seconds,
  );
};

const shouldShowUpcomingEvent = (event, now) => {
  const datePart = parseDatePart(event?.startDate);
  if (!datePart) return false;

  const eventDateKey = `${String(datePart.year).padStart(4, '0')}-${String(datePart.month).padStart(2, '0')}-${String(datePart.day).padStart(2, '0')}`;
  const todayKey = getLocalDateKey(now);

  if (eventDateKey > todayKey) return true;
  if (eventDateKey < todayKey) return false;

  // For current day: keep event visible until one hour after start time.
  const startDateTime = getEventStartDateTime(event);
  if (!startDateTime) return true;
  const visibleUntil = new Date(startDateTime.getTime() + 60 * 60 * 1000);
  return now <= visibleUntil;
};

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    const fetchUpcomingEvents = async () => {
      try {
        const response = await EventService.fetchDraftEvents();
        if (response.data?.success) {
          const now = new Date();
          const visibleUpcomingEvents = (response.data.data || [])
            .filter((e) => e.isDraft === false)
            .filter((e) => shouldShowUpcomingEvent(e, now))
            .sort((a, b) => {
              const aStart = getEventStartDateTime(a);
              const bStart = getEventStartDateTime(b);
              return (bStart?.getTime() || 0) - (aStart?.getTime() || 0);
            });
          setEvents(visibleUpcomingEvents);
        } else {
          setError('Failed to fetch upcoming events.');
        }
      } catch (err) {
        console.error('API error:', err);
        setError('An error occurred while fetching events.');
      } finally {
        setLoading(false);
      }
    };
    fetchUpcomingEvents();
  }, []);

  // Pagination logic
  const indexOfLast = currentPage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const currentEvents = events.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(events.length / pageSize);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const onEditEvent = (eventId) => {
    const url = `/create-events/${eventId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onShareEvent = (eventId) => {
    const url = `/create-events/${eventId}?tab=CreateAds`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onMeetingEvent = (link) => {
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      alert("Meeting link not available.");
    }
  };

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header className="fw-bold d-flex justify-content-between align-items-center">
        Upcoming Events
        <Form.Select
          value={pageSize}
          onChange={handlePageSizeChange}
          style={{ width: "100px" }}
          size="sm"
        >
          {[5, 10, 20, 50, 100].map(size => (
            <option key={size} value={size}>{size} / page</option>
          ))}
        </Form.Select>
      </Card.Header>

      {loading ? (
        <div className="p-3 text-center">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : error ? (
        <Alert variant="danger" className="m-3">
          {error}
        </Alert>
      ) : (
        <>
          <Table bordered hover>
            <thead>
              <tr>
                <th>S.NO</th>
                <th>Event Name</th>
                <th>Date & Time</th>
                <th>Duration</th>
                <th>Meeting Link</th>
                <th>Edit</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {currentEvents.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted">
                    No upcoming events found.
                  </td>
                </tr>
              ) : (
                currentEvents.map((event, idx) => (
                  <tr key={event.id}>
                    <td>{indexOfFirst + idx + 1}</td>
                    <td>{event.eventName}</td>
                    <td>
                      {event.startDate
                        ? `${new Date(event.startDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })} ${
                            event.eventTime
                              ? `at ${formatTo12Hour(convertUTCToIST_HHMMSS(event.eventTime))}`
                              : ''
                          }`
                        : (event.eventTime
                          ? formatTo12Hour(convertUTCToIST_HHMMSS(event.eventTime))
                          : '-')}
                    </td>
                    <td>
                      {event.durationInMinutes
                        ? `${event.durationInMinutes} min`
                        : '-'}
                    </td>
                    <td>
                      <button
                        className="btn btn-link p-0"
                        style={{
                          color: '#0d6efd',
                          textDecoration: 'underline',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => onMeetingEvent(event.meetingLink)}
                      >
                        Join
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-link p-0"
                        style={{
                          color: '#0d6efd',
                          textDecoration: 'underline',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => onEditEvent(event.id)}
                      >
                        Edit
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-link p-0"
                        style={{
                          color: '#0d6efd',
                          textDecoration: 'underline',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => onShareEvent(event.id)}
                      >
                        Share
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              {[...Array(totalPages)].map((_, idx) => (
                <Pagination.Item
                  key={idx + 1}
                  active={idx + 1 === currentPage}
                  onClick={() => handlePageChange(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          )}
        </>
      )}
    </Card>
  );
};

export default UpcomingEvents;
